/**
 * Product-event beacon → awesome-chat-api `POST /api/v2/events`.
 *
 * Backend/funnel events (messages sent, recommendation impressions, errors,
 * onboarding funnel) are already counted server-side. This beacon fills the gap
 * for signals only the client can see — widget/chat opens and recommendation
 * click-through — folding them into the same Prometheus/Loki product-event sink.
 *
 * The endpoint is PUBLIC and deliberately strict: only the names in
 * {@link ProductEvent} are accepted (anything else is a 400, never a new metric
 * label), and `props` are scalar + size-capped server-side. This is a fire-and-
 * forget beacon — failures are swallowed, it never blocks or throws into the UI.
 *
 * NOTE: distinct from `behaviorAnalytics`/`analytics` (→ `.NET /api/analytics/
 * events`, the personalization pipeline). Different service, schema and sink.
 */

import { CHAT_API_BASE_URL } from '../constants/chat';

/** The exact event names awesome-chat-api's /api/v2/events accepts. */
export const ProductEvents = {
  widgetOpened: 'widget.opened',
  chatOpened: 'chat.opened',
  // CORE-1753 — приложение или сайт вышли на передний план у вошедшего
  // человека. На него chat-api решает, пора ли Агапелле написать первой.
  appOpened: 'app.opened',
  recommendationClicked: 'recommendation.clicked',
  onboardingTourStep: 'onboarding.tour_step',
  onboardingTourSkipped: 'onboarding.tour_skipped',
  onboardingTourCompleted: 'onboarding.tour_completed',
} as const;

export type ProductEvent = (typeof ProductEvents)[keyof typeof ProductEvents];

/**
 * `props.surface` события `chat.opened`: какой ИИ-чат открыт.
 *
 * CORE-1753 — одна функция на веб и натив. Событие запускает на chat-api
 * приветствие при возвращении, и до этой задачи его слала только веб-панель;
 * натив, начав слать то же самое, должен называть поверхность теми же словами,
 * иначе разрезы по `surface` в аналитике разъедутся по платформам.
 */
export function aiChatSurface(entityType: 'ai' | 'wisdom-council', companionId: string): string {
  return entityType === 'wisdom-council' ? 'wisdom_council' : companionId;
}

type Scalar = string | number | boolean | null;

/**
 * Emit one product event. Best-effort: never awaits into the UI path, never
 * throws. `userId` is optional (the endpoint keys funnels on it when present).
 */
export function emitProductEvent(
  event: ProductEvent,
  opts?: { userId?: string; props?: Record<string, Scalar> },
): void {
  const base = CHAT_API_BASE_URL.replace(/\/+$/, '');
  const body = JSON.stringify({
    event,
    user_id: opts?.userId ?? '',
    props: opts?.props ?? {},
  });
  try {
    // keepalive lets the POST outlive a navigation/tab-close on web; harmless on native.
    void fetch(`${base}/api/v2/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* analytics beacon — swallow */
  }
}
